import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import {
  UserIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";

const ElencoVolontari = () => {
  const { activeCongregazione, user } = useAuth();
  const [volontari, setVolontari] = useState([]); // Solo la pagina corrente
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    stato: "",
    sesso: "",
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
    scope: null,
  });
  const [summary, setSummary] = useState({ totale: 0, uomini: 0, donne: 0, attivi: 0 });
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDetailVolunteer, setMobileDetailVolunteer] = useState(null);
  const [delegati, setDelegati] = useState([]);
  const [loadingDelegati, setLoadingDelegati] = useState(false);
  const canViewEmail = user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const canViewDeleghe = canViewEmail;

  const scope = `${user?.id || ""}:${activeCongregazione?.id || user?.congregazione_id || ""}:${user?.ruolo || ""}`;
  const page = pagination.scope === scope ? pagination.page : 1;

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    const fetchVolontari = async () => {
      setLoading(true);
      try {
        const response = await api.get("/volontari", {
          signal: controller.signal,
          params: {
            page,
            limit: pagination.limit,
            stato: filters.stato || undefined,
            sesso: filters.sesso || undefined,
            search: filters.search || undefined,
          },
        });
        if (!current) return;
        const data = response.data;
        const pages = Math.max(1, data.pagination.pages);
        setVolontari(data.volontari);
        setSummary(data.summary);
        setPagination((prev) => ({ ...prev, scope, page: Math.min(page, pages), total: data.pagination.total, pages }));
        setHasLoaded(true);
      } catch (error) {
        if (!current) return;
        setVolontari([]);
        setSummary({ totale: 0, uomini: 0, donne: 0, attivi: 0 });
        setPagination((prev) => ({ ...prev, total: 0, pages: 1 }));
        toast.error("Errore nel caricamento dei volontari");
      } finally {
        if (current) setLoading(false);
      }
    };
    fetchVolontari();
    return () => { current = false; controller.abort(); };
  }, [scope, page, pagination.limit, filters.stato, filters.sesso, filters.search]);

  const fetchDelegati = useCallback(async () => {
    if (!canViewDeleghe || !activeCongregazione?.id) {
      setDelegati([]);
      return;
    }
    setLoadingDelegati(true);
    try {
      const response = await api.get(
        `/deleghe?congregazione_id=${activeCongregazione.id}`
      );
      setDelegati(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Errore nel caricamento dei delegati:", error);
      setDelegati([]);
    } finally {
      setLoadingDelegati(false);
    }
  }, [activeCongregazione, canViewDeleghe]);

  useEffect(() => {
    fetchDelegati();
  }, [fetchDelegati]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // La ricerca viene eseguita sull'intera congregazione dal server.
  useEffect(() => {
    const search = searchInput.trim();
    if (search === filters.search) return;
    const timer = window.setTimeout(() => {
      setFilters((prev) => ({ ...prev, search }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, filters.search]);

  const getStatusColor = (stato) => {
    return stato === "attivo"
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  const getSessoIcon = (sesso) => {
    return sesso === "M" ? "👨" : "👩";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Mai";
    const date = new Date(dateString);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearch = (searchTerm) => {
    // Aggiorna immediatamente il valore dell'input
    setSearchInput(searchTerm);
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage,
      scope,
    }));
  };

  const paginatedVolontari = volontari;
  const totalPages = pagination.pages;

  const clearFilters = () => {
    setFilters({
      stato: "",
      sesso: "",
      search: "",
    });
    setSearchInput("");
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  if (loading && !hasLoaded) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Caricamento volontari...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Elenco Proclamatori
        </h1>
        <p className="text-gray-600 mt-1">
          Visualizza tutti i proclamatori registrati nel sistema
        </p>
        <div className="accent-line mt-3" />
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-100">
              <UserIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {searchInput.trim() || filters.stato || filters.sesso
                  ? "Risultati"
                  : "Totali"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.totale}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-purple-100">
              <UserIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {searchInput.trim() || filters.stato || filters.sesso
                  ? "Uomini (filtro)"
                  : "Uomini"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.uomini}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-pink-100">
              <UserIcon className="h-6 w-6 text-pink-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {searchInput.trim() || filters.stato || filters.sesso
                  ? "Donne (filtro)"
                  : "Donne"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.donne}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-100">
              <UserIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {searchInput.trim() || filters.stato || filters.sesso
                  ? "Attivi (filtro)"
                  : "Attivi"}
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {summary.attivi}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <FunnelIcon className="h-5 w-5 mr-2" />
            Filtri
          </h3>
          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancella filtri
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="form-label">Stato</label>
            <select
              className="form-input py-2"
              value={filters.stato}
              onChange={(e) => handleFilterChange("stato", e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="attivo">Attivo</option>
              <option value="non_attivo">Non Attivo</option>
            </select>
          </div>
          <div>
            <label className="form-label">Sesso</label>
            <select
              className="form-input py-2"
              value={filters.sesso}
              onChange={(e) => handleFilterChange("sesso", e.target.value)}
            >
              <option value="">Tutti</option>
              <option value="M">Uomini</option>
              <option value="F">Donne</option>
            </select>
          </div>
          <div>
            <label className="form-label">Cerca per nome</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Nome o cognome..."
                className="form-input pl-10 py-2"
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>
          <div className="flex items-end space-x-4">
            <div className="text-sm text-gray-600">
              Risultati: {pagination.total} volontari
            </div>
            <div>
              <label className="form-label">Mostra</label>
              <select
                className="form-input py-2"
                value={pagination.limit}
                onChange={(e) => {
                  setPagination((prev) => ({
                    ...prev,
                    limit: parseInt(e.target.value),
                    page: 1,
                  }));
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista Volontari */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Elenco Volontari ({pagination.total})
          </h3>
        </div>
        <div className="accent-line mx-6" />
        {loading && <p role="status" className="px-6 py-3 text-sm text-gray-600">Aggiornamento elenco...</p>}
        {isMobile ? (
          <div className="divide-y divide-gray-200">
            {paginatedVolontari.map((volontario) => (
              <div
                key={volontario.id}
                className="relative p-4 pl-6 space-y-3 transition hover:bg-gray-50 hover:shadow-sm"
              >
                <span
                  className="accent-bar top-4 bottom-4"
                  aria-hidden="true"
                />
                <div className="flex items-center">
                  <div className="text-3xl mr-3">
                    {getSessoIcon(volontario.sesso)}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      {volontario.nome} {volontario.cognome}
                    </p>
                    <p className="text-xs text-gray-500">ID: {volontario.id}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs uppercase text-gray-500">Stato</p>
                    <span
                      className={`inline-flex px-2 py-0.5 mt-1 rounded-full text-xs font-semibold ${getStatusColor(
                        volontario.stato
                      )}`}
                    >
                      {volontario.stato === "attivo" ? "Attivo" : "Non Attivo"}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-gray-500">Sesso</p>
                    <p className="mt-1 text-gray-900">
                      {volontario.sesso === "M" ? "Uomo" : "Donna"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-gray-500">
                      Ultima assegnazione
                    </p>
                    <p className="mt-1 text-gray-900">
                      {formatDate(volontario.ultima_assegnazione)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase text-gray-500">
                      Turni completati
                    </p>
                    <p className="mt-1 text-gray-900">
                      {volontario.turni_completati || "0"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setMobileDetailVolunteer(volontario)}
                  className="w-full px-4 py-2 text-sm font-medium text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50"
                >
                  Dettagli
                </button>
              </div>
            ))}
            {paginatedVolontari.length === 0 && (
              <div className="p-6 text-center text-gray-500 text-sm">
                Nessun volontario trovato per i filtri selezionati.
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header min-w-[150px]">Volontario</th>
                  <th className="table-header">Contatti</th>
                  <th className="table-header">Sesso</th>
                  <th className="table-header">Stato</th>
                  <th className="table-header">Ultima Assegnazione</th>
                  <th className="table-header">Turni Completati</th>
                  <th className="table-header">Ruolo</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedVolontari.map((volontario) => (
                  <tr key={volontario.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap min-w-[150px] relative pl-6">
                      <span
                        className="accent-bar top-3 bottom-3"
                        aria-hidden="true"
                      />
                      <div className="flex items-center">
                        <div className="text-2xl mr-3">
                          {getSessoIcon(volontario.sesso)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {volontario.nome} {volontario.cognome}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {volontario.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {canViewEmail && (
                        <div className="text-sm text-gray-900">
                          {volontario.email || "N/A"}
                        </div>
                      )}
                      <div
                        className={`text-sm ${
                          canViewEmail ? "text-gray-500" : "text-gray-900"
                        }`}
                      >
                        {volontario.telefono || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {volontario.sesso === "M" ? "Uomo" : "Donna"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          volontario.stato
                        )}`}
                      >
                        {volontario.stato === "attivo" ? "Attivo" : "Non Attivo"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(volontario.ultima_assegnazione)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {volontario.turni_completati || "0"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          volontario.ruolo === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {volontario.ruolo === "admin"
                          ? "Amministratore"
                          : "Volontario"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div
              className={`flex ${
                isMobile
                  ? "flex-col space-y-3 text-sm text-center"
                  : "items-center justify-between"
              }`}
            >
              <div className={`${isMobile ? "" : "text-sm"} text-gray-700`}>
                Mostrando{" "}
                {pagination.total > 0
                  ? (page - 1) * pagination.limit + 1
                  : 0}{" "}
                a{" "}
                {Math.min(
                  page * pagination.limit,
                  pagination.total
                )}{" "}
                di {pagination.total} risultati
              </div>
              <div
                className={`flex ${
                  isMobile ? "flex-col space-y-2" : "space-x-2"
                }`}
              >
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={loading || page === 1}
                  className={`px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 ${
                    isMobile ? "w-full" : ""
                  }`}
                >
                  Precedente
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Pagina {page} di {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={loading || page === totalPages}
                  className={`px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 ${
                    isMobile ? "w-full" : ""
                  }`}
                >
                  Successiva
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delegati */}
      {canViewDeleghe && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Elenco Delegati ({delegati.length})
            </h3>
            <p className="text-sm text-gray-500">
              {activeCongregazione
                ? `Deleghe attive per ${activeCongregazione.nome}`
                : "Seleziona una congregazione per visualizzare le deleghe"}
            </p>
          </div>
          <button
            onClick={fetchDelegati}
            disabled={loadingDelegati || !activeCongregazione?.id}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingDelegati ? "Aggiornamento..." : "Aggiorna elenco"}
          </button>
        </div>
        <div className="accent-line mx-6" />
        {loadingDelegati ? (
          <div className="px-6 py-10 flex items-center justify-center text-gray-500 text-sm">
            Caricamento delegati...
          </div>
        ) : !activeCongregazione?.id ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            Nessuna congregazione attiva selezionata.
          </div>
        ) : delegati.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            Non ci sono deleghe attive per questa congregazione.
          </div>
        ) : isMobile ? (
          <div className="divide-y divide-gray-200">
            {delegati.map((delegato) => (
              <div
                key={delegato.id}
                className="relative p-4 pl-6 space-y-3 transition hover:bg-gray-50 hover:shadow-sm"
              >
                <span
                  className="accent-bar top-4 bottom-4"
                  aria-hidden="true"
                />
                <div>
                  <p className="text-base font-semibold text-gray-900">
                    {delegato.volontario_nome} {delegato.volontario_cognome}
                  </p>
                  <p className="text-xs text-gray-500">
                    ID volontario: {delegato.volontario_id}
                  </p>
                </div>
                <div className="text-sm text-gray-700 space-y-1">
                  <p>
                    Ruolo:{" "}
                    <span className="font-medium">
                      {delegato.ruolo_delega === "admin"
                        ? "Amministratore"
                        : "Volontario"}
                    </span>
                  </p>
                  <p>
                    Congregazione di origine:{" "}
                    <span className="font-medium">
                      {delegato.congregazione_codice} -{" "}
                      {delegato.congregazione_nome}
                    </span>
                  </p>
                  <p>{delegato.volontario_email || "Nessuna email"}</p>
                  <p>{delegato.volontario_telefono || "Nessun telefono"}</p>
                  {delegato.note && (
                    <p className="text-gray-500 text-xs">Note: {delegato.note}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Delegato</th>
                  <th className="table-header">Congregazione di origine</th>
                  <th className="table-header">Ruolo delega</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Telefono</th>
                  <th className="table-header">Note</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {delegati.map((delegato) => (
                  <tr key={delegato.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap relative pl-6">
                      <span
                        className="accent-bar top-3 bottom-3"
                        aria-hidden="true"
                      />
                      <div className="text-sm font-medium text-gray-900">
                        {delegato.volontario_nome} {delegato.volontario_cognome}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {delegato.volontario_id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {delegato.congregazione_codice}
                      </div>
                      <div className="text-sm text-gray-500">
                        {delegato.congregazione_nome}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          delegato.ruolo_delega === "admin"
                            ? "bg-purple-100 text-purple-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {delegato.ruolo_delega === "admin"
                          ? "Amministratore"
                          : "Volontario"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {delegato.volontario_email || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {delegato.volontario_telefono || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {delegato.note || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {isMobile && mobileDetailVolunteer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-md p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Dettagli {mobileDetailVolunteer.nome} {mobileDetailVolunteer.cognome}
              </h3>
              <button
                onClick={() => setMobileDetailVolunteer(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <div className="space-y-4 text-sm text-gray-700">
              <div>
                <p className="text-xs uppercase text-gray-500">Contatti</p>
                {canViewEmail && (
                  <p>{mobileDetailVolunteer.email || "N/A"}</p>
                )}
                <p className={canViewEmail ? "text-gray-500" : "text-gray-900"}>
                  {mobileDetailVolunteer.telefono || "N/A"}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs uppercase text-gray-500">Ruolo</p>
                  <p>
                    {mobileDetailVolunteer.ruolo === "admin"
                      ? "Amministratore"
                      : "Volontario"}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">Stato</p>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(
                      mobileDetailVolunteer.stato
                    )}`}
                  >
                    {mobileDetailVolunteer.stato === "attivo"
                      ? "Attivo"
                      : "Non Attivo"}
                  </span>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">
                    Ultima assegnazione
                  </p>
                  <p>{formatDate(mobileDetailVolunteer.ultima_assegnazione)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-gray-500">
                    Turni completati
                  </p>
                  <p>{mobileDetailVolunteer.turni_completati || "0"}</p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Coniuge</p>
                {mobileDetailVolunteer.coniuge_nome ? (
                  <p className="text-gray-900">
                    {mobileDetailVolunteer.coniuge_nome}
                  </p>
                ) : (
                  <p className="text-gray-500">Nessuno</p>
                )}
              </div>
            </div>
            <div className="mt-6">
              <button
                onClick={() => setMobileDetailVolunteer(null)}
                className="w-full px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElencoVolontari;
